import { IMainPhoto } from 'redhotpie-shared-lib/interfaces/IUser';
import userApi from '../api/userApi';
import * as Constant from '../constants/constants';
import * as messageUtils from '../functions/messageUtils';
import { mappingParamGetUsersActive } from '../functions/utils';
import AccountSettingsProfile from '../models/accountSettingsProfile';
import eventDetail from '../models/eventDetail';
import memberProfile from '../models/memberProfile';
import photoModel from '../models/photo';
import placeModel from '../models/place';
import QuestionAnswerModel from '../models/questionAnswerModel';
import RecentlySentPhotoModel from '../models/recentlySentPhotoModel';
import User from '../models/user';
import userProfileSummaryModel from '../models/userProfileSummary';
import { mappingVideoFile } from '../models/videoFile';
import Consumable from '../models/consumable';
import TravelPlan from '../models/travelPlans';
import { IParamsRequest } from '../interfaces/IAction';
import { CreateUserInvite } from '../types/invites';
import { IApiResponse } from '../interfaces/IApiResponse';
import { InviteType } from '../enum/InviteEnum';
import { IInviteCredit } from '../models/consumable';
import NotificationGroupInvite from 'redhotpie-shared-lib/models/notification';
import Photo from '../models/photo';
import { IUserLoginInfo } from '../interfaces/IUser';

const buffer = require('buffer');

class UserService {
  async login(credential: { username: string; pwd: string }) {
    // const b = new buffer.Buffer(`${credential.username}:${credential.pwd}`);
    // const encodedAuth = b.toString('base64');
    return userApi.login();
  }

  getFacebookUserInfo(token: string) {
    return userApi.getFacebookUserInfo(token);
  }

  register(user: any, cb: () => void) {
    setTimeout(cb, 1000);
  }

  async getUsersProfile(params: any) {
    const res = await userApi.getUsersProfile(params);
    if (res.success && res.data) {
      return {
        success: true,
        data: User.mappingObjectsCustom(res.data.ProfileSummaries),
      };
    }
    console.error('ERROR: userService/getUsersProfile:', res.message);
    return res;
  }

  async getUsersProfileMemberSearch(params: any) {
    const res = await userApi.getUsersProfile(params);
    if (res.success && res.data.ProfileSummaries?.length > 0) {
      return {
        success: true,
        data: {
          users: memberProfile.mappingObjectsCustomMemberSearch(res.data.ProfileSummaries),
          searchGuid: res.data.SearchGuid,
          pageSize: res.data.PageSize,
          pageNumber: res.data.PageNumber,
          type: res.data.Type,
          sorting: res.data.Sorting,
          moreResults: res.data.MoreResults,
        },
      };
    }
    return {
      success: res.success,
      data: { users: [] },
    };
  }

  async getAutoCompletePlace(place: string, type: string, key?: string, query?: string) {
    const queryParams = query || `&types=${type}`;
    const response = await userApi.getAutoCompletePlace(place, key || Constant.KEY.GOOGLE_API_KEY || '', queryParams);
    if (response?.data?.predictions?.length > 0) {
      return placeModel.mappingObjects(response.data.predictions);
    }
    return [];
  }

  async getDetailPlace(place: any, key?: string) {
    const response = await userApi.getDetailPlace(place.placeId, key || Constant.KEY.GOOGLE_API_KEY || '');
    if (response?.data?.result?.geometry?.location && response.data.result.address_components) {
      const { location } = response.data.result.geometry;
      const placeDetail = { ...place };
      response.data.result.address_components.forEach((component: any) => {
        if (component.types[0] === 'country') {
          placeDetail.countryCode = component.short_name;
        }
      });
      const postalCode = response.data.result.address_components.find((item: any) => item.types.includes('postal_code'))?.long_name;
      placeDetail.stateName = response.data.result.name || response.data.result.formatted_address;
      placeDetail.lat = location.lat;
      placeDetail.lng = location.lng;
      placeDetail.postalCode = postalCode;
      return { success: true, message: response.message, data: placeDetail };
    }
    return { success: false, message: response.message, data: null };
  }

  async getQuickProfileByXmppId(xmppId: string) {
    xmppId = messageUtils.addPrefixAndSuffix(xmppId);
    try {
      const res = await userApi.getQuickProfileByXmppId(xmppId);
      if (res.success) {
        const { vCard } = res.data;
        const data = {
          id: res.data.id,
          userId: parseInt(res.data.userId),
          xmppId: res.data.xmppId,
          avatarUrl: vCard?.avatarUrl || '',
          age: vCard?.age || '',
          location: '',
          firstName: vCard?.firstName || '',
          lastName: vCard?.lastName || '',
          favorites: [],
          online: vCard?.online || '',
          memberTypeTitle: vCard?.memberTypeTitle || 'Man',
          ageTitle: vCard?.ageTitle || '',
          pinned: [],
          displayName: vCard?.displayName || '',
          matching: [],
        };
        res.data = User.mappingObject(data);
      } else {
        console.error('ERROR: userService/getQuickProfileByXmppId:', res.message);
      }
      return res;
    } catch (error) {
      console.error('ERROR: userService/getQuickProfileByXmppId:', error);
      throw new Error('failed');
    }
  }

  async getMyQuickProfile() {
    try {
      const res = await userApi.getMyQuickProfile();
      if (res.success) {
        const { vCard } = res.data;
        const data = {
          id: res.data.id,
          userId: parseInt(res.data.userId),
          xmppId: res.data.xmppId,
          avatarUrl: vCard?.avatarUrl || '',
          age: vCard?.age || '',
          location: '',
          firstName: vCard?.firstName || '',
          lastName: vCard?.lastName || '',
          favorites: [],
          online: vCard?.online || '',
          memberTypeTitle: vCard?.memberTypeTitle || 'Man',
          ageTitle: vCard?.ageTitle || '',
          pinned: [],
          displayName: vCard?.displayName || '',
          matching: [],
        };
        res.data = User.mappingObject(data);
      } else {
        console.error('ERROR: userService/getMyQuickProfile:', res.message);
      }
      return res;
    } catch (error) {
      console.error('ERROR: userService/getMyQuickProfile:', error);
      throw new Error('failed');
    }
  }

  async getUserXmppDetail() {
    try {
      const res = await userApi.getUserXmppDetail();
      if (!res.success) {
        console.error('ERROR: userService/getUserXmppDetail:', res.message);
      }
      return res;
    } catch (e) {
      console.error(e);
    }
  }

  async getAllFriendsFavorites(params: any, results: any) {
    const body = { type: params.type || Constant.PROFILE_SEARCH_TYPE.Active };
    params.pageSize = params.pageSize || 100;
    params.pageNumber = params.pageNumber || 1;
    try {
      const res = await userApi.getFriendsFavorites(params, body);
      if (res.success) {
        return {
          success: true,
          pageSize: res.data.pageSize,
          pageNumber: res.data.pageNumber,
          data: { items: User.mappingObjectsCustomFriendAndFavorite(res.data.items) },
        };
      }
      console.error('ERROR: userService/getAllFriendsFavorites:', res.message);
      return res;
    } catch (e) {
      console.error('ERROR: userService/getAllFriendsFavorites:', e);
      throw new Error('failed');
    }
  }

  async getProfileSummary(profileId: number) {
    try {
      const res = await userApi.getProfileSummary(profileId);
      if (res.success) {
        return { success: true, data: User.mappingObjectCustom(res.data) };
      }
      console.error('ERROR: userService/getProfileSummary:', res.message);
      return res;
    } catch (e) {
      console.error('ERROR: userService/getProfileSummary:', e);
      throw new Error('failed');
    }
  }

  async getUserProfileSummary(buildVersion?: string) {
    try {
      const res = await userApi.getUserProfileSummary(buildVersion);
      if (res.success) {
        return { success: true, data: userProfileSummaryModel.mappingObjectCustom(res.data) };
      }
      console.error('ERROR: userService/getUserProfileSummary:', res.message);
      return res;
    } catch (e) {
      console.error('ERROR: userService/getUserProfileSummary:', e);
      throw new Error('failed');
    }
  }

  async getUserMainPhoto(): Promise<IApiResponse<Partial<IMainPhoto>>> {
    try {
      const res = await userApi.getUserMainPhoto();
      if (res.success) {
        return { success: true, data: userProfileSummaryModel.mappingPhoto(res.data) };
      }
      console.error('ERROR: userService/getUserMainPhoto:', res.message);
      return res;
    } catch (e) {
      console.error('ERROR: userService/getUserMainPhoto:', e);
      return { success: false };
    }
  }

  async getMainPhoto(profileId: number): Promise<{
    success: boolean;
    data?: IMainPhoto;
  }> {
    try {
      const res = await userApi.getMainPhoto(profileId);
      if (res.success) {
        return { success: true, data: res.data ? photoModel.mappingObject(res.data) : ({} as IMainPhoto) };
      }
      console.error('ERROR: userService/getMainPhoto:', res.message);
      return res;
    } catch (e) {
      console.error('ERROR: userService/getMainPhoto:', e);
      return { success: false };
    }
  }

  async getMainPhotos(profileIds: number[]): Promise<{
    success: boolean;
    data?: IMainPhoto[];
    userIDs?: number[];
  }> {
    try {
      const res = await userApi.getMainPhotos(profileIds.toString());
      if (res.success) {
        return {
          success: true,
          data: res.data ? photoModel.mappingCustomObjects(Object.values(res.data)) : [],
          userIDs: Object.keys(res.data).map(id => Number(id)),
        };
      }
      console.error('ERROR: userService/getMainPhoto:', res.message);
      return res;
    } catch (e) {
      console.error('ERROR: userService/getMainPhoto:', e);
      throw new Error('failed');
    }
  }

  async addFavorite(userId: string) {
    const res = await userApi.addFavorite(userId);
    if (!res.success) {
      console.error('ERROR: userService/addFavorite:', res.message);
    }
    return res;
  }

  async removeFavorite(userId: string) {
    const params = { profiles: [userId] };
    const res = await userApi.removeFavorite(params);
    if (!res.success) {
      console.error('ERROR: userService/removeFavorite:', res.message);
    }
    return res;
  }

  async blockUser(userId: string) {
    const res = await userApi.blockUser(userId);
    if (!res.success) {
      console.warn('ERROR: userService/blockUser:', res.message);
    } else {
      res.data = { id: userId };
    }
    return res;
  }

  async blockUserChat(userId: string) {
    const res = await userApi.blockUserChat(userId);
    if (!res.success) {
      console.warn('ERROR: userService/blockUserChat:', res.message);
    } else {
      res.data = { id: userId };
    }
    return res;
  }

  async getMatchingUsers() {
    const res = await userApi.getMatchingUsers();
    if (!res.success) {
      console.warn('ERROR: userService/blockUser:', res.message);
    }
    return res;
  }

  async addMatchingUser(xmppId: string) {
    const params = { xmppId: messageUtils.addPrefixAndSuffix(xmppId), status: true };
    const res = await userApi.addMatchingUser(params);
    if (!res.success) {
      console.warn('ERROR: userService/addMatchingUser:', res.message);
    }
    return res;
  }

  async getUserSettings() {
    const res = await userApi.getUserSettings();
    if (!res.success) {
      console.warn('ERROR: userService/getUserSettings:', res.message);
    }
    return res;
  }

  async patchUserClientSettings(params: any) {
    const res = await userApi.patchUserClientSettings(params);
    if (!res.success) {
      console.warn('ERROR: userService/patchUserClientSettings:', res.message);
    }
    return res;
  }

  async patchUserMessengerSettings(params: any) {
    const res = await userApi.patchUserMessengerSettings(params);
    if (!res.success) {
      console.warn('patchClientMessengerSettings:', res.message);
    }
    return res;
  }

  async patchUserChatRoomSettings(params: any) {
    const res = await userApi.patchUserChatRoomSettings(params);
    if (!res.success) {
      console.warn('patchClientMessengerSettings:', res.message);
    }
    return res;
  }

  async addReport(report: any) {
    const res = await userApi.addReport(report);
    if (!res.success) {
      console.warn('AddReportUserError:', res.message);
    }
    return res;
  }

  getUserWhoBlockMeList() {
    return userApi.getUserWhoBlockMeList();
  }

  async getUsersBlocked(): Promise<IApiResponse<IUserLoginInfo[]>> {
    const response = await userApi.getUsersBlocked();
    if (response.success) {
      const resDatas = response.data?.items.map((i: any) => ({ userId: i }));
      response.data = resDatas ? User.mappingObjects(resDatas) : [];
    } else {
      console.error('ERROR: userService/getUsersBlocked:');
    }
    return response;
  }

  getUserStatus(userId: number) {
    return userApi.getUserStatus(userId);
  }

  checkUserOnline(xmppId: string) {
    return userApi.checkUserOnline(xmppId);
  }

  async updateUserLocation(param: { lat?: number; lng?: number; offset: number }) {
    const body = { Latitude: param.lat, Longitude: param.lng, Offset: param.offset };
    const response = await userApi.updateUserLocation(body);
    if (response.success) {
      return { success: true };
    }
    return { success: false };
  }

  async patchUpdateAccountSetting(params: any) {
    const res = await userApi.patchUpdateAccountSetting(params);
    return {
      success: res.success,
      data: AccountSettingsProfile.mappingObject(res.data),
      message: res.message,
    };
  }

  async patchUpdateAccountTooltips(id: string) {
    const res = await userApi.patchUpdateAccountTooltips(id);
    return {
      success: res.success,
      data: res.data,
      message: res.message,
    };
  }

  async createUserToolTip(toolTipId: number) {
    const response = await userApi.createUserToolTip(toolTipId);
    if (!response.success) {
      console.error('ERROR: userService/createUserToolTip:');
    }
    return response;
  }

  async updateUserSummaryTigase(userId: string, params: any) {
    const response = await userApi.updateUserSummaryTigase(userId, params);
    if (!response.success) {
      console.error('ERROR: userService/updateUserSummaryTigase:');
    }
    return response;
  }

  async checkIsThisUserMe(userId: number) {
    const response = await userApi.checkIsThisUserMe(userId);
    if (!response.success) {
      console.error('ERROR: userService/checkIsThisUserMe:');
    }
    return response;
  }

  async getUsersActive(params: any) {
    const body = mappingParamGetUsersActive(params);
    const res = await userApi.getUsersActive(body);
    try {
      const data = User.mappingObjectsCustom(res.data.items);
      return { success: res.success, data, message: res.message };
    } catch (e) {
      console.error('ERROR: userService/getUsersActive:', e);
      return { success: false, message: res.message };
    }
  }

  async getActiveUserStateCount(googleMapPlaceID?: string | undefined) {
    const params = googleMapPlaceID ? { GoogleMapPlaceID: googleMapPlaceID } : {};
    const res = await userApi.getActiveUserStateCount(params);
    if (res.success) {
      return { success: res.success, data: eventDetail.mappingEventPopularCounts(res.data.items, googleMapPlaceID) };
    }
    console.error('ERROR: eventService/getEventPopularCount:', res.message);
    return res;
  }

  async createUserAppRating(score: number) {
    const res = await userApi.createUserAppRating({ Score: score });
    if (!res.success) {
      console.error('ERROR: eventService/getEventPopularCount:', res.message);
    }
    return { success: res.success };
  }

  async getAboutRhp() {
    const res = await userApi.getAboutRhp();
    try {
      return { success: res.success, data: QuestionAnswerModel.mappingPropertyObjects(res.data.items) };
    } catch (e) {
      console.error('ERROR: userService/getAboutRhp: ', e);
      return { success: false, message: 'ERROR: userService/getAboutRhp' };
    }
  }

  async getSearchFriendsOnline(params: any) {
    const body = { Page: params?.page || 1, Size: params?.size || 20 };
    const res = await userApi.getSearchFriendsOnline(body);
    try {
      return { success: res.success, data: User.mappingObjectsUserFriendActive(res.data.items) };
    } catch (e) {
      console.error('ERROR: userService/getSearchFriendsOnline: ', e);
      return { success: false, message: e };
    }
  }

  async getSearchFavouriteOnline(params: any) {
    const body = { Page: params?.page || 1, Size: params?.size || 20 };
    const res = await userApi.getSearchFavouriteOnline(body);
    try {
      return { success: res.success, data: User.mappingObjectsUserFriendActive(res.data.items) };
    } catch (e) {
      console.error('ERROR: userService/getSearchFavouriteOnline: ', e);
      return { success: false };
    }
  }

  async getUserSharedPhotos(params: any) {
    const body = { Page: params?.page || 1, Size: params?.size || Constant.PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH };
    const res = await userApi.getUserSharedPhotos(body);
    try {
      return {
        success: res.success,
        data: RecentlySentPhotoModel.mappingListMedia(res.data?.Data || []),
        paginationInfo: RecentlySentPhotoModel.mappingPaginationInfo(res.data?.PaginationInfo),
      };
    } catch (e) {
      console.error('ERROR: userService/getUserSharedPhotos: ', e);
      return { success: false };
    }
  }

  async patchUserSharedPhotos(params: string[]) {
    const body = { PhotoIDs: params };
    const res = await userApi.patchUserSharedPhotos(body);
    try {
      return res;
    } catch (e) {
      console.error('ERROR: userService/patchUserSharedPhotos: ', e);
    }
  }
  deleteUserVideos(ids: string[]): Promise<any> {
    let queryParams = ids.map(id => `&VideoIds=${id}&`).join('');
    queryParams = queryParams.slice(1, queryParams.length - 1);
    return userApi.deleteUserVideos(queryParams).then((res: any) => {
      try {
        return res;
      } catch (e) {
        console.error('ERROR: userService/deleteUserVideos: ', e);
        return { success: false };
      }
    });
  }

  getProfileVideo(ownerUserId: string, videoId: string): Promise<any> {
    return userApi.getProfileVideo(ownerUserId, videoId).then((res: any) => {
      try {
        return { success: true, data: mappingVideoFile(res.data) };
      } catch (e) {
        console.error('ERROR: userService/getProfileVideo: ', e);
        return { success: false };
      }
    });
  }

  getProfileAudio(ownerUserId: string, fileName: string): Promise<any> {
    return userApi.getProfileAudio(ownerUserId, fileName).then((res: any) => {
      try {
        return { success: true, data: res.data };
      } catch (e) {
        console.error('ERROR: userService/getProfileAudio: ', e);
        return { success: false };
      }
    });
  }

  deleteParentOwnedComment(params: any): Promise<any> {
    return userApi.deleteParentOwnedComment(params).then((res: any) => {
      try {
        return res;
      } catch (e) {
        console.error('ERROR: userService/deleteParentOwnedComment: ', e);
        return { success: false };
      }
    });
  }

  getManualPhoneVerificationCode(): Promise<any> {
    return userApi.getManualPhoneVerificationCode().then((res: any) => {
      try {
        return { success: res.success, message: res.message, data: res.data?.Result || '' };
      } catch (e) {
        console.error('ERROR: userService/getManualPhoneVerificationCode: ', e);
        return { success: false };
      }
    });
  }

  getActiveSubmissionCount(params: any): Promise<any> {
    return userApi.getActiveSubmissionCount(params).then((res: any) => {
      try {
        return { success: res.success, data: res.data };
      } catch (e) {
        console.error('ERROR: userService/getActiveSubmissionCount: ', e);
        return { success: false };
      }
    });
  }

  getServerTime(): Promise<any> {
    return userApi.getServerTime().then((res: any) => {
      try {
        return { success: res.success, data: res.data };
      } catch (e) {
        console.error('ERROR: userService/getServerTime: ', e);
        return { success: false };
      }
    });
  }

  getUserLivenessSessionId(): Promise<any> {
    return userApi.getUserLivenessSessionId().then((res: any) => {
      try {
        return { success: res.success, data: res.data };
      } catch (e) {
        console.error('ERROR: userService/getUserLivenessSessionId: ', e);
        return { success: false };
      }
    });
  }

  getUserLivenessResult(sessionId: string): Promise<any> {
    return userApi.getUserLivenessResult(sessionId).then((res: any) => {
      try {
        console.log('res', res);
        return { success: res.success, data: res.data };
      } catch (e) {
        console.error('ERROR: userService/getUserLivenessResult: ', e);
        return { success: false };
      }
    });
  }

  getUserProfileBoostStats(): Promise<any> {
    return userApi.getUserProfileBoostStats().then((res: any) => {
      try {
        const data = {
          views: res?.data?.Views || 0,
          likes: res?.data?.Likes || 0,
          messages: res?.data?.Messages || 0,
          viewsPercent: res?.data?.ViewsPercent || 0,
          likesPercent: res?.data?.LikesPercent || 0,
          messagesPercent: res?.data?.MessagesPercent || 0,
        };
        return { success: res.success, data };
      } catch (e) {
        console.error('ERROR: userService/getUserProfileBoostStats: ', e);
        return { success: false };
      }
    });
  }

  async createUserInvite(params: CreateUserInvite): Promise<
    IApiResponse<{
      inviteResponse: InviteType;
      status?: IInviteCredit;
      authData?: {
        isReceiverMatching: boolean;
        isSenderMatching: boolean;
        senderMemberLevel: number;
        receiverMemberLevel: number;
      };
    }>
  > {
    return userApi.createUserInvite(params).then((res: any) => {
      try {
        if (res.success) {
          return {
            success: true,
            data: {
              inviteResponse: res.data.InviteResponse,
              status: Consumable.mappingUserRemainingInvitesCredit(res.data.Status),
              authData: {
                isReceiverMatching: res.data.IsReceiverMatching,
                isSenderMatching: res.data.IsSenderMatching,
                senderMemberLevel: res.data.SenderMemberLevel,
                receiverMemberLevel: res.data.ReceiverMemberLevel,
              },
            },
          };
        } else {
          return { success: false, data: { inviteResponse: res.data.InviteResponse } };
        }
      } catch (e) {
        console.error('ERROR: userService/createUserInvite: ', e);
        return { success: false, data: { inviteResponse: res.data.InviteResponse } };
      }
    });
  }

  async getReceivedInvites(params: any) {
    return userApi.getReceivedInvites(params).then((res: any) => {
      try {
        return {
          success: res.success,
          data: {
            items: NotificationGroupInvite.mappingList(res.data.Invites),
          },
          paginationInfo: {
            page: res.data.Page,
            size: res.data.Size,
            total: res.data.TotalItems,
            totalPages: res.data.TotalPages,
          },
        };
      } catch (e) {
        console.error('ERROR: userService/getReceivedInvites: ', e);
        return { success: false, data: { items: [] } };
      }
    });
  }

  async deleteReceivedInvites(inviteID: string) {
    return userApi.deleteReceivedInvites(inviteID).then((res: any) => {
      try {
        return res;
      } catch (e) {
        console.error('ERROR: userService/deleteReceivedInvites: ', e);
        return { success: false };
      }
    });
  }

  async postAffiliateTraffic(params: { affiliateId: string; referer: string }) {
    const body = {
      Affiliate: params.affiliateId,
      Referer: params.referer,
    };
    return userApi.postAffiliateTraffic(body).then((res: any) => {
      try {
        return res;
      } catch (e) {
        console.error('ERROR: userService/postAffiliateTraffic: ', e);
        return { success: false };
      }
    });
  }
}

export default new UserService();
